import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Settings2, Shield, Save, Loader2, Plug, Mail, Sparkles, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPage() {
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingAI, setTestingAI] = useState(false);
  const [aiTestResult, setAiTestResult] = useState(null);
  const [testingSMTP, setTestingSMTP] = useState(false);
  const [smtpTestEmail, setSmtpTestEmail] = useState('');
  const [smtpTestResult, setSmtpTestResult] = useState(null);
  const [dnsChecking, setDnsChecking] = useState(false);
  const [dnsResults, setDnsResults] = useState(null);
  const [dnsDomain, setDnsDomain] = useState('torr.pl');

  const fetchData = useCallback(async () => {
    try {
      const [u, s] = await Promise.all([api.get('/admin/users'), api.get('/admin/settings')]);
      setUsers(u.data.items);
      setSettings(s.data);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Brak uprawnien administratora');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleUser = async (userId, isActive) => {
    try { await api.put(`/admin/users/${userId}`, { is_active: !isActive }); toast.success(isActive ? 'Dezaktywowany' : 'Aktywowany'); fetchData(); }
    catch { toast.error('Blad'); }
  };

  const changeRole = async (userId, role) => {
    try { await api.put(`/admin/users/${userId}`, { role }); toast.success('Rola zmieniona'); fetchData(); }
    catch { toast.error('Blad'); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try { await api.put('/admin/settings', settings); toast.success('Ustawienia zapisane'); }
    catch { toast.error('Blad zapisu'); }
    finally { setSaving(false); }
  };

  const updateNested = (path, value) => {
    const parts = path.split('.');
    setSettings(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      let obj = copy;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!obj[parts[i]]) obj[parts[i]] = {};
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return copy;
    });
  };

  const updateNum = (path, value) => updateNested(path, parseInt(value) || 0);

  const testAI = async () => {
    setTestingAI(true);
    setAiTestResult(null);
    try {
      await saveSettings();
      const r = await api.post('/admin/ai/test', {
        provider: settings?.ai?.default_provider,
        model: settings?.ai?.default_model
      });
      setAiTestResult({ ok: true, message: r.data.response });
      toast.success('Test AI przeszedl!');
    } catch (err) {
      setAiTestResult({ ok: false, message: err.response?.data?.detail || 'Blad polaczenia' });
      toast.error('Test AI nie powiodl sie');
    } finally { setTestingAI(false); }
  };

  const testSMTP = async () => {
    if (!smtpTestEmail) { toast.error('Podaj adres email do testu'); return; }
    setTestingSMTP(true);
    setSmtpTestResult(null);
    try {
      await saveSettings();
      const r = await api.post('/admin/smtp/test', { to_email: smtpTestEmail });
      setSmtpTestResult({ ok: true, message: r.data.message });
      toast.success('Email testowy wyslany!');
    } catch (err) {
      setSmtpTestResult({ ok: false, message: err.response?.data?.detail || 'Blad SMTP' });
      toast.error('Test SMTP nie powiodl sie');
    } finally { setTestingSMTP(false); }
  };

  const checkDNS = async () => {
    setDnsChecking(true);
    setDnsResults(null);
    try {
      const r = await api.post('/admin/dns/check', { domain: dnsDomain });
      setDnsResults(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad sprawdzania DNS'); }
    finally { setDnsChecking(false); }
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Panel administracyjny</h1>
        <p className="text-sm text-muted-foreground mt-1">Uzytkownicy, integracje i ustawienia</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="users"><Users className="h-3.5 w-3.5 mr-1.5" />Uzytkownicy</TabsTrigger>
          <TabsTrigger value="integrations"><Plug className="h-3.5 w-3.5 mr-1.5" />Integracje</TabsTrigger>
          <TabsTrigger value="email"><Mail className="h-3.5 w-3.5 mr-1.5" />Email</TabsTrigger>
          <TabsTrigger value="settings"><Settings2 className="h-3.5 w-3.5 mr-1.5" />Ustawienia</TabsTrigger>
          <TabsTrigger value="security"><Shield className="h-3.5 w-3.5 mr-1.5" />Bezpieczenstwo</TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-4">
          <Card className="border-border/50 bg-card/80">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Imie</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Rola</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data rejestracji</TableHead>
                    <TableHead className="w-24">Aktywny</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id} className="hover:bg-muted/30 transition-colors duration-150">
                      <TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                      <TableCell>
                        <Select value={u.role} onValueChange={v => changeRole(u.id, v)}>
                          <SelectTrigger className="w-28 h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="affiliate">Afiliant</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={u.is_active ? 'border-[hsl(var(--success))]/30 text-[hsl(var(--success))]' : 'text-muted-foreground'}>
                          {u.is_active ? 'Aktywny' : 'Nieaktywny'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground">{u.created_at?.slice(0, 10)}</TableCell>
                      <TableCell><Switch checked={u.is_active} onCheckedChange={() => toggleUser(u.id, u.is_active)} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="mt-4">
          <div className="space-y-6">
            {/* AI Configuration */}
            <Card className="border-border/50 bg-card/80">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" />
                  <CardTitle className="font-heading text-base">Konfiguracja AI</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/30">
                  <Switch
                    checked={settings?.ai?.use_emergent_key ?? true}
                    onCheckedChange={v => updateNested('ai.use_emergent_key', v)}
                    data-testid="ai-use-emergent-key"
                  />
                  <div>
                    <p className="text-sm font-medium">Uzyj klucza Emergent (Universal Key)</p>
                    <p className="text-xs text-muted-foreground">Wbudowany klucz — nie wymaga konfiguracji. Odznacz aby uzyc wlasnych kluczy API.</p>
                  </div>
                </div>

                {!settings?.ai?.use_emergent_key && (
                  <div className="space-y-4 pl-4 border-l-2 border-border/30">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Klucz OpenAI API</Label>
                        <Input
                          type="password"
                          value={settings?.ai?.openai_api_key || ''}
                          onChange={e => updateNested('ai.openai_api_key', e.target.value)}
                          placeholder="sk-..."
                          data-testid="ai-openai-key-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Klucz Grok (xAI) API</Label>
                        <Input
                          type="password"
                          value={settings?.ai?.grok_api_key || ''}
                          onChange={e => updateNested('ai.grok_api_key', e.target.value)}
                          placeholder="xai-..."
                          data-testid="ai-grok-key-input"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Domyslny provider</Label>
                    <Select value={settings?.ai?.default_provider || 'openai'} onValueChange={v => updateNested('ai.default_provider', v)}>
                      <SelectTrigger data-testid="ai-provider-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="openai">OpenAI</SelectItem>
                        <SelectItem value="grok">Grok (xAI)</SelectItem>
                        <SelectItem value="anthropic">Anthropic</SelectItem>
                        <SelectItem value="gemini">Google Gemini</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Model</Label>
                    <Select value={settings?.ai?.default_model || 'gpt-4o'} onValueChange={v => updateNested('ai.default_model', v)}>
                      <SelectTrigger data-testid="ai-model-input"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {settings?.ai?.default_provider === 'grok' ? (
                          <>
                            <SelectItem value="grok-3">Grok 3</SelectItem>
                            <SelectItem value="grok-3-mini">Grok 3 Mini</SelectItem>
                            <SelectItem value="grok-2">Grok 2</SelectItem>
                            <SelectItem value="grok-2-mini">Grok 2 Mini</SelectItem>
                          </>
                        ) : settings?.ai?.default_provider === 'anthropic' ? (
                          <>
                            <SelectItem value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5</SelectItem>
                            <SelectItem value="claude-4-sonnet-20250514">Claude 4 Sonnet</SelectItem>
                            <SelectItem value="claude-3-5-haiku-20241022">Claude 3.5 Haiku</SelectItem>
                          </>
                        ) : settings?.ai?.default_provider === 'gemini' ? (
                          <>
                            <SelectItem value="gemini-2.5-pro">Gemini 2.5 Pro</SelectItem>
                            <SelectItem value="gemini-2.5-flash">Gemini 2.5 Flash</SelectItem>
                            <SelectItem value="gemini-2.0-flash">Gemini 2.0 Flash</SelectItem>
                          </>
                        ) : (
                          <>
                            <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                            <SelectItem value="gpt-4.1">GPT-4.1</SelectItem>
                            <SelectItem value="gpt-4.1-mini">GPT-4.1 Mini</SelectItem>
                            <SelectItem value="gpt-5.1">GPT-5.1</SelectItem>
                            <SelectItem value="o4-mini">o4-mini</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={testAI} disabled={testingAI} data-testid="ai-test-button">
                    {testingAI ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                    Testuj polaczenie AI
                  </Button>
                  {aiTestResult && (
                    <div className={`flex items-center gap-2 text-xs ${aiTestResult.ok ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--destructive))]'}`}>
                      {aiTestResult.ok ? <CheckCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                      <span className="max-w-xs truncate">{aiTestResult.message}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* SMTP Configuration */}
            <Card className="border-border/50 bg-card/80">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  <CardTitle className="font-heading text-base">Konfiguracja serwera pocztowego (SMTP)</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/30">
                  <Switch
                    checked={settings?.smtp?.enabled ?? false}
                    onCheckedChange={v => updateNested('smtp.enabled', v)}
                    data-testid="smtp-enabled-switch"
                  />
                  <div>
                    <p className="text-sm font-medium">Wlacz wysylke email</p>
                    <p className="text-xs text-muted-foreground">Aktywuj po skonfigurowaniu serwera SMTP</p>
                  </div>
                </div>

                <Separator />
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Serwer SMTP</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2 sm:col-span-2">
                    <Label className="text-xs">Host serwera</Label>
                    <Input value={settings?.smtp?.host || ''} onChange={e => updateNested('smtp.host', e.target.value)} placeholder="smtp.torr.pl / mail.torr.pl" data-testid="smtp-host-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Port</Label>
                    <Input type="number" value={settings?.smtp?.port || 587} onChange={e => updateNested('smtp.port', parseInt(e.target.value) || 587)} data-testid="smtp-port-input" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Uzytkownik (login)</Label>
                    <Input value={settings?.smtp?.username || ''} onChange={e => updateNested('smtp.username', e.target.value)} placeholder="user@torr.pl" data-testid="smtp-username-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Haslo</Label>
                    <Input type="password" value={settings?.smtp?.password || ''} onChange={e => updateNested('smtp.password', e.target.value)} data-testid="smtp-password-input" />
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch checked={settings?.smtp?.use_tls ?? true} onCheckedChange={v => updateNested('smtp.use_tls', v)} data-testid="smtp-tls-switch" />
                    <Label className="text-xs">STARTTLS</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={settings?.smtp?.use_ssl ?? false} onCheckedChange={v => updateNested('smtp.use_ssl', v)} data-testid="smtp-ssl-switch" />
                    <Label className="text-xs">SSL</Label>
                  </div>
                </div>

                <Separator />
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Nadawca</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Nazwa nadawcy</Label>
                    <Input value={settings?.smtp?.from_name || ''} onChange={e => updateNested('smtp.from_name', e.target.value)} placeholder="Torr.pl" data-testid="smtp-from-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Email nadawcy</Label>
                    <Input value={settings?.smtp?.from_email || ''} onChange={e => updateNested('smtp.from_email', e.target.value)} placeholder="noreply@torr.pl" data-testid="smtp-from-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Reply-To</Label>
                    <Input value={settings?.smtp?.reply_to || ''} onChange={e => updateNested('smtp.reply_to', e.target.value)} placeholder="kontakt@torr.pl" data-testid="smtp-reply-to-input" />
                  </div>
                </div>

                <Separator />
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Test polaczenia</p>
                <div className="flex items-center gap-3">
                  <Input value={smtpTestEmail} onChange={e => setSmtpTestEmail(e.target.value)} placeholder="twoj@email.pl" className="max-w-xs" data-testid="smtp-test-email-input" />
                  <Button variant="outline" onClick={testSMTP} disabled={testingSMTP} data-testid="smtp-test-button">
                    {testingSMTP ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                    Wyslij testowy email
                  </Button>
                </div>
                {smtpTestResult && (
                  <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg ${smtpTestResult.ok ? 'bg-[hsl(var(--success))]/5 text-[hsl(var(--success))]' : 'bg-[hsl(var(--destructive))]/5 text-[hsl(var(--destructive))]'}`}>
                    {smtpTestResult.ok ? <CheckCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                    <span>{smtpTestResult.message}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Button onClick={saveSettings} disabled={saving} data-testid="admin-save-integrations">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Zapisz konfiguracje integracji
            </Button>
          </div>
        </TabsContent>


        {/* Email Tab */}
        <TabsContent value="email" className="mt-4">
          <div className="space-y-6">
            {/* DNS Authentication Checker */}
            <Card className="border-border/50 bg-card/80">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <CardTitle className="font-heading text-base">Weryfikacja uwierzytelnienia domeny</CardTitle>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Sprawdz czy domena ma poprawnie skonfigurowane SPF, DKIM, DMARC i BIMI (wg wytycznych AnoMail)</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Input value={dnsDomain} onChange={e => setDnsDomain(e.target.value)} placeholder="torr.pl" className="max-w-xs" data-testid="dns-domain-input" />
                  <Button variant="outline" onClick={checkDNS} disabled={dnsChecking} data-testid="dns-check-button">
                    {dnsChecking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
                    Sprawdz DNS
                  </Button>
                </div>
                {dnsResults && (
                  <div className="space-y-3">
                    {['spf', 'dkim', 'dmarc', 'bimi', 'mx'].map(key => {
                      const r = dnsResults[key];
                      if (!r) return null;
                      const isOk = r.status === 'ok';
                      const isWarn = r.status === 'weak' || r.status === 'unknown';
                      return (
                        <div key={key} className={`p-3 rounded-lg border ${isOk ? 'bg-[hsl(var(--success))]/5 border-[hsl(var(--success))]/20' : isWarn ? 'bg-[hsl(var(--warning))]/5 border-[hsl(var(--warning))]/20' : 'bg-[hsl(var(--destructive))]/5 border-[hsl(var(--destructive))]/20'}`}>
                          <div className="flex items-center gap-2">
                            {isOk ? <CheckCircle className="h-4 w-4 text-[hsl(var(--success))]" /> : <AlertTriangle className={`h-4 w-4 ${isWarn ? 'text-[hsl(var(--warning))]' : 'text-[hsl(var(--destructive))]'}`} />}
                            <span className="text-sm font-medium uppercase">{key}</span>
                            <Badge variant="outline" className={`text-xs ml-auto ${isOk ? 'text-[hsl(var(--success))]' : isWarn ? 'text-[hsl(var(--warning))]' : 'text-[hsl(var(--destructive))]'}`}>{r.status}</Badge>
                          </div>
                          {r.record && <p className="text-xs font-mono text-muted-foreground mt-1 break-all">{typeof r.record === 'string' ? r.record : JSON.stringify(r.record)}</p>}
                          {r.records && <p className="text-xs font-mono text-muted-foreground mt-1">{r.records.join(', ')}</p>}
                          {r.hint && <p className="text-xs text-muted-foreground mt-1">{r.hint}</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Email Sending Config */}
            <Card className="border-border/50 bg-card/80">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  <CardTitle className="font-heading text-base">Ustawienia wysylki email</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Max emaili / godzine</Label>
                    <Input type="number" value={settings?.email_config?.max_per_hour || 100} onChange={e => updateNum('email_config.max_per_hour', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Opoznienie miedzy emailami (s)</Label>
                    <Input type="number" value={settings?.email_config?.delay_between_emails || 2} onChange={e => updateNum('email_config.delay_between_emails', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Max emaili / dzien</Label>
                    <Input type="number" value={settings?.email_config?.max_per_day || 5000} onChange={e => updateNum('email_config.max_per_day', e.target.value)} />
                  </div>
                </div>
                <Separator />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Rozgrzewanie IP (warm-up)</p>
                  <p className="text-xs text-muted-foreground mb-3">Zalecany plan rozgrzewki nowego IP/domeny wg AnoMail:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[{d:'Dzien 1-3', v:'50/dzien'},{d:'Dzien 4-7', v:'200/dzien'},{d:'Tydzien 2', v:'500/dzien'},{d:'Tydzien 3+', v:'Pelna moc'}].map(w => (
                      <div key={w.d} className="p-2 rounded bg-muted/30 border border-border/30 text-center">
                        <p className="text-xs text-muted-foreground">{w.d}</p>
                        <p className="text-sm font-medium font-mono">{w.v}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <Separator />
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Obowiazkowe elementy emaila</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-[hsl(var(--success))]" />Link rezygnacji (unsubscribe) - dodawany automatycznie w stopce</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-[hsl(var(--success))]" />Naglowek List-Unsubscribe (one-click) - dodawany automatycznie</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-[hsl(var(--success))]" />Adres abuse@torr.pl w stopce</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-3 w-3 text-[hsl(var(--success))]" />Message-ID z domena torr.pl</li>
                    <li className="flex items-center gap-2"><AlertTriangle className="h-3 w-3 text-[hsl(var(--warning))]" />Podpis S/MIME - wymaga certyfikatu (opcjonalnie)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Button onClick={saveSettings} disabled={saving} data-testid="admin-save-email">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Zapisz ustawienia email
            </Button>
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-4">
          {settings && (
            <div className="space-y-6">
              <Card className="border-border/50 bg-card/80">
                <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Domyslne limity bramek</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    <div className="space-y-2"><Label className="text-xs">Max/min</Label><Input type="number" value={settings.default_gateway_limits?.max_per_minute || 0} onChange={e => updateNum('default_gateway_limits.max_per_minute', e.target.value)} /></div>
                    <div className="space-y-2"><Label className="text-xs">Max/godz</Label><Input type="number" value={settings.default_gateway_limits?.max_per_hour || 0} onChange={e => updateNum('default_gateway_limits.max_per_hour', e.target.value)} /></div>
                    <div className="space-y-2"><Label className="text-xs">Max/dzien</Label><Input type="number" value={settings.default_gateway_limits?.max_per_day || 0} onChange={e => updateNum('default_gateway_limits.max_per_day', e.target.value)} /></div>
                    <div className="space-y-2"><Label className="text-xs">Min delay (s)</Label><Input type="number" value={settings.default_gateway_limits?.delay_min_seconds || 0} onChange={e => updateNum('default_gateway_limits.delay_min_seconds', e.target.value)} /></div>
                    <div className="space-y-2"><Label className="text-xs">Max delay (s)</Label><Input type="number" value={settings.default_gateway_limits?.delay_max_seconds || 0} onChange={e => updateNum('default_gateway_limits.delay_max_seconds', e.target.value)} /></div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border/50 bg-card/80">
                <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Punktacja aktywnosci</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="space-y-2"><Label className="text-xs">Klikniecie</Label><Input type="number" value={settings.scoring?.click || 0} onChange={e => updateNum('scoring.click', e.target.value)} /></div>
                    <div className="space-y-2"><Label className="text-xs">Otwarcie</Label><Input type="number" value={settings.scoring?.open || 0} onChange={e => updateNum('scoring.open', e.target.value)} /></div>
                    <div className="space-y-2"><Label className="text-xs">Odpowiedz</Label><Input type="number" value={settings.scoring?.reply || 0} onChange={e => updateNum('scoring.reply', e.target.value)} /></div>
                    <div className="space-y-2"><Label className="text-xs">Brak aktyw./mies.</Label><Input type="number" value={settings.scoring?.inactivity_monthly || 0} onChange={e => updateNum('scoring.inactivity_monthly', e.target.value)} /></div>
                  </div>
                </CardContent>
              </Card>
              <Button onClick={saveSettings} disabled={saving} data-testid="admin-save-settings">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Zapisz ustawienia
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="mt-4">
          <Card className="border-border/50 bg-card/80">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Slowa kluczowe opt-out</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">Wiadomosci zawierajace te slowa sa automatycznie klasyfikowane jako opt-out:</p>
              <div className="flex flex-wrap gap-2">
                {(settings?.blacklist_keywords || []).map((kw, i) => (
                  <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                ))}
              </div>
              <Separator className="my-4" />
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">RODO / Compliance</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>- Eksport danych kontaktu: Kontakty &rarr; Profil &rarr; Eksport RODO</li>
                  <li>- Usuwanie danych kontaktu: Kontakty &rarr; Profil &rarr; Usun dane (RODO)</li>
                  <li>- Log usuniecia: kolekcja gdpr_logs w bazie danych</li>
                  <li>- Auto-blacklist przy OPT_OUT (AI klasyfikacja)</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
